import { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  FormControlLabel,
  Switch,
  Paper,
  Button,
  Divider,
  Collapse,
  Alert,
  Menu,
  MenuItem,
  ListItemText,
  Autocomplete,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
} from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PreviewIcon from '@mui/icons-material/Preview'
import EventIcon from '@mui/icons-material/Event'
import {
  ScheduleFrequency,
  DayOfWeek,
  OccurrenceType,
  DAY_OF_WEEK_LABELS,
  DAY_OF_WEEK_FULL_LABELS,
  OCCURRENCE_TYPE_LABELS,
  MONTH_SHORT_LABELS,
  TIME_SCHEDULE_TEMPLATES,
  DEFAULT_TIME_SCHEDULE_CONFIG,
  TIMEZONE_OPTIONS,
  formatUtcOffset,
  type TimeScheduleConfig,
  type TimezoneOption,
} from '@features/adventures/types/workflow.types'

interface TimeScheduleConfigPanelProps {
  config: Record<string, unknown>
  onChange: (config: Record<string, unknown>) => void
  isReadOnly?: boolean
}

// Helper to get config value with defaults
function getConfigValue<T>(config: Record<string, unknown>, key: keyof TimeScheduleConfig, defaultValue: T): T {
  if (key in config && config[key] !== undefined) {
    return config[key] as T
  }
  return defaultValue
}

// Helper to get the Nth occurrence of a day in a month (e.g., 2nd Monday)
function getNthDayOfMonth(year: number, month: number, dayOfWeek: DayOfWeek, occurrence: OccurrenceType): Date | null {
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)

  if (occurrence === OccurrenceType.Last) {
    // Find the last occurrence of dayOfWeek in the month
    let day = lastDay.getDate()
    while (day > 0) {
      const date = new Date(year, month, day)
      if (date.getDay() === dayOfWeek) {
        return date
      }
      day--
    }
    return null
  }

  // Find the Nth occurrence
  let count = 0
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day)
    if (date.getDay() === dayOfWeek) {
      count++
      if (count === occurrence + 1) { // occurrence is 0-indexed (First=0, Second=1, etc.)
        return date
      }
    }
  }
  return null
}

// Calculate next N occurrences based on schedule config
function calculateNextOccurrences(
  frequency: ScheduleFrequency,
  startDateStr: string | undefined,
  endDateStr: string | undefined,
  selectedDays: DayOfWeek[],
  monthlyPatternType: 'specific' | 'relative',
  selectedMonthDays: number[],
  selectedOccurrences: OccurrenceType[],
  selectedMonths: number[],
  count: number = 10
): Date[] {
  const occurrences: Date[] = []
  const now = new Date()
  now.setHours(0, 0, 0, 0)

  const scheduleStart = startDateStr ? new Date(startDateStr) : now
  const scheduleEnd = endDateStr ? new Date(endDateStr) : null

  // Start from today or schedule start, whichever is later
  let currentDate = new Date(Math.max(now.getTime(), scheduleStart.getTime()))
  currentDate.setHours(0, 0, 0, 0)

  // Limit search to 2 years to prevent infinite loops
  const maxDate = new Date(now)
  maxDate.setFullYear(maxDate.getFullYear() + 2)

  const isValidDate = (date: Date): boolean => {
    if (scheduleEnd && date > scheduleEnd) return false
    if (date > maxDate) return false
    return true
  }

  const matchesSchedule = (date: Date): boolean => {
    const dayOfWeek = date.getDay() as DayOfWeek
    const dayOfMonth = date.getDate()
    const month = date.getMonth() + 1 // 1-12

    switch (frequency) {
      case ScheduleFrequency.OneTime:
        // For one-time, just check if within date range
        return true

      case ScheduleFrequency.Daily:
        // Check if day of week matches (if days are selected)
        if (selectedDays.length > 0 && !selectedDays.includes(dayOfWeek)) {
          return false
        }
        return true

      case ScheduleFrequency.Weekly:
        return selectedDays.length === 0 || selectedDays.includes(dayOfWeek)

      case ScheduleFrequency.Monthly:
        if (monthlyPatternType === 'specific') {
          return selectedMonthDays.length === 0 || selectedMonthDays.includes(dayOfMonth)
        } else {
          // Relative pattern - check if this date matches any occurrence/day combo
          if (selectedOccurrences.length === 0 || selectedDays.length === 0) return true

          for (const occ of selectedOccurrences) {
            for (const dow of selectedDays) {
              const nthDay = getNthDayOfMonth(date.getFullYear(), date.getMonth(), dow, occ)
              if (nthDay && nthDay.getDate() === dayOfMonth && nthDay.getDay() === dayOfWeek) {
                return true
              }
            }
          }
          return false
        }

      case ScheduleFrequency.Yearly:
        // Check month first
        if (selectedMonths.length > 0 && !selectedMonths.includes(month)) {
          return false
        }
        // Then check day pattern (same as monthly)
        if (monthlyPatternType === 'specific') {
          return selectedMonthDays.length === 0 || selectedMonthDays.includes(dayOfMonth)
        } else {
          if (selectedOccurrences.length === 0 || selectedDays.length === 0) return true

          for (const occ of selectedOccurrences) {
            for (const dow of selectedDays) {
              const nthDay = getNthDayOfMonth(date.getFullYear(), date.getMonth(), dow, occ)
              if (nthDay && nthDay.getDate() === dayOfMonth && nthDay.getDay() === dayOfWeek) {
                return true
              }
            }
          }
          return false
        }

      default:
        return true
    }
  }

  // For one-time events, just return the start date if it's in the future
  if (frequency === ScheduleFrequency.OneTime) {
    if (startDateStr) {
      const start = new Date(startDateStr)
      if (start >= now && isValidDate(start)) {
        occurrences.push(start)
      }
    }
    return occurrences
  }

  // Iterate through dates to find matches
  while (occurrences.length < count && isValidDate(currentDate)) {
    if (matchesSchedule(currentDate)) {
      occurrences.push(new Date(currentDate))
    }
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return occurrences
}

// Map DayOfWeek to iCal day codes for byDay field
const DAY_OF_WEEK_TO_ICAL: Record<DayOfWeek, string> = {
  [DayOfWeek.Sunday]: 'SU',
  [DayOfWeek.Monday]: 'MO',
  [DayOfWeek.Tuesday]: 'TU',
  [DayOfWeek.Wednesday]: 'WE',
  [DayOfWeek.Thursday]: 'TH',
  [DayOfWeek.Friday]: 'FR',
  [DayOfWeek.Saturday]: 'SA',
}

// Convert selectedDays array to byDay string (comma-separated iCal codes)
function selectedDaysToByDay(days: DayOfWeek[]): string {
  return days.map((d) => DAY_OF_WEEK_TO_ICAL[d]).join(',')
}

export function TimeScheduleConfigPanel({ config, onChange, isReadOnly = false }: TimeScheduleConfigPanelProps) {
  // Extract values from config with defaults
  const frequency = getConfigValue(config, 'frequency', DEFAULT_TIME_SCHEDULE_CONFIG.frequency)
  const startDate = getConfigValue<string | undefined>(config, 'startDate', undefined)
  const endDate = getConfigValue<string | undefined>(config, 'endDate', undefined)
  const startTime = getConfigValue<string | undefined>(config, 'startTime', undefined)
  const endTime = getConfigValue<string | undefined>(config, 'endTime', undefined)
  const selectedDays = getConfigValue(config, 'selectedDays', DEFAULT_TIME_SCHEDULE_CONFIG.selectedDays)
  const monthlyPatternType = getConfigValue(config, 'monthlyPatternType', DEFAULT_TIME_SCHEDULE_CONFIG.monthlyPatternType)
  const selectedMonthDays = getConfigValue(config, 'selectedMonthDays', DEFAULT_TIME_SCHEDULE_CONFIG.selectedMonthDays)
  const selectedOccurrences = getConfigValue(config, 'selectedOccurrences', DEFAULT_TIME_SCHEDULE_CONFIG.selectedOccurrences)
  const selectedMonths = getConfigValue(config, 'selectedMonths', DEFAULT_TIME_SCHEDULE_CONFIG.selectedMonths)
  const useUserLocalTime = getConfigValue(config, 'useUserLocalTime', DEFAULT_TIME_SCHEDULE_CONFIG.useUserLocalTime)
  const timezone = getConfigValue<string | undefined>(config, 'timezone', undefined)

  // Template menu state
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<null | HTMLElement>(null)

  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false)

  // Calculate next occurrences
  const nextOccurrences = useMemo(() => {
    return calculateNextOccurrences(
      frequency,
      startDate,
      endDate,
      selectedDays,
      monthlyPatternType,
      selectedMonthDays,
      selectedOccurrences,
      selectedMonths,
      10
    )
  }, [frequency, startDate, endDate, selectedDays, monthlyPatternType, selectedMonthDays, selectedOccurrences, selectedMonths])

  // Update helpers - automatically sync byDay when selectedDays changes
  const updateConfig = (updates: Partial<TimeScheduleConfig>) => {
    const newConfig = { ...config, ...updates }

    // If selectedDays was updated, sync to byDay for API compatibility
    if ('selectedDays' in updates) {
      const days = updates.selectedDays as DayOfWeek[]
      ;(newConfig as Record<string, unknown>).byDay = selectedDaysToByDay(days)
    }

    onChange(newConfig)
  }

  // Handle frequency change
  const handleFrequencyChange = (_: React.MouseEvent<HTMLElement>, newFrequency: ScheduleFrequency | null) => {
    if (newFrequency !== null) {
      // Reset relevant fields when frequency changes
      const updates: Partial<TimeScheduleConfig> & { byDay?: string } = {
        frequency: newFrequency,
        selectedDays: [],
        selectedMonthDays: [],
        selectedOccurrences: [],
        selectedMonths: [],
      }
      // Also reset byDay when frequency changes
      onChange({ ...config, ...updates, byDay: '' })
    }
  }

  // Handle day toggle
  const toggleDay = (day: DayOfWeek) => {
    const newDays = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day].sort((a, b) => a - b)
    updateConfig({ selectedDays: newDays })
  }

  // Handle month day toggle
  const toggleMonthDay = (day: number) => {
    const newDays = selectedMonthDays.includes(day)
      ? selectedMonthDays.filter((d) => d !== day)
      : [...selectedMonthDays, day].sort((a, b) => a - b)
    updateConfig({ selectedMonthDays: newDays })
  }

  // Handle occurrence toggle
  const toggleOccurrence = (occurrence: OccurrenceType) => {
    const newOccurrences = selectedOccurrences.includes(occurrence)
      ? selectedOccurrences.filter((o) => o !== occurrence)
      : [...selectedOccurrences, occurrence].sort((a, b) => a - b)
    updateConfig({ selectedOccurrences: newOccurrences })
  }

  // Handle month toggle
  const toggleMonth = (month: number) => {
    const newMonths = selectedMonths.includes(month)
      ? selectedMonths.filter((m) => m !== month)
      : [...selectedMonths, month].sort((a, b) => a - b)
    updateConfig({ selectedMonths: newMonths })
  }

  // Quick select helpers
  const selectWeekdays = () => {
    updateConfig({
      selectedDays: [DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday, DayOfWeek.Thursday, DayOfWeek.Friday],
    })
  }

  const selectWeekends = () => {
    updateConfig({ selectedDays: [DayOfWeek.Saturday, DayOfWeek.Sunday] })
  }

  const selectAllDays = () => {
    updateConfig({
      selectedDays: [
        DayOfWeek.Sunday, DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday,
        DayOfWeek.Thursday, DayOfWeek.Friday, DayOfWeek.Saturday,
      ],
    })
  }

  // Apply template
  const applyTemplate = (template: typeof TIME_SCHEDULE_TEMPLATES[0]) => {
    onChange({
      ...DEFAULT_TIME_SCHEDULE_CONFIG,
      ...template.config,
    })
    setTemplateMenuAnchor(null)
  }

  // Generate human-readable description
  const getDescription = (): string => {
    const parts: string[] = []

    // Date range
    if (startDate || endDate) {
      if (startDate && endDate) {
        parts.push(`${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`)
      } else if (startDate) {
        parts.push(`Starting ${new Date(startDate).toLocaleDateString()}`)
      } else if (endDate) {
        parts.push(`Until ${new Date(endDate).toLocaleDateString()}`)
      }
    }

    // Frequency and days
    switch (frequency) {
      case ScheduleFrequency.OneTime:
        if (!startDate && !endDate) parts.push('One-time event')
        break
      case ScheduleFrequency.Daily:
        if (selectedDays.length === 7 || selectedDays.length === 0) {
          parts.push('Every day')
        } else if (selectedDays.length === 5 && !selectedDays.includes(DayOfWeek.Saturday) && !selectedDays.includes(DayOfWeek.Sunday)) {
          parts.push('Weekdays')
        } else if (selectedDays.length === 2 && selectedDays.includes(DayOfWeek.Saturday) && selectedDays.includes(DayOfWeek.Sunday)) {
          parts.push('Weekends')
        } else {
          parts.push(selectedDays.map((d) => DAY_OF_WEEK_LABELS[d]).join(', '))
        }
        break
      case ScheduleFrequency.Weekly:
        if (selectedDays.length > 0) {
          parts.push(`Weekly on ${selectedDays.map((d) => DAY_OF_WEEK_LABELS[d]).join(', ')}`)
        } else {
          parts.push('Weekly')
        }
        break
      case ScheduleFrequency.Monthly:
        if (monthlyPatternType === 'specific' && selectedMonthDays.length > 0) {
          parts.push(`Monthly on day${selectedMonthDays.length > 1 ? 's' : ''} ${selectedMonthDays.join(', ')}`)
        } else if (monthlyPatternType === 'relative' && selectedOccurrences.length > 0 && selectedDays.length > 0) {
          const occLabels = selectedOccurrences.map((o) => OCCURRENCE_TYPE_LABELS[o]).join(', ')
          const dayLabels = selectedDays.map((d) => DAY_OF_WEEK_FULL_LABELS[d]).join(', ')
          parts.push(`${occLabels} ${dayLabels} of each month`)
        } else {
          parts.push('Monthly')
        }
        break
      case ScheduleFrequency.Yearly:
        if (selectedMonths.length > 0) {
          let yearlyDesc = `In ${selectedMonths.map((m) => MONTH_SHORT_LABELS[m - 1]).join(', ')}`
          // Add day pattern info
          if (monthlyPatternType === 'specific' && selectedMonthDays.length > 0) {
            yearlyDesc += ` on day${selectedMonthDays.length > 1 ? 's' : ''} ${selectedMonthDays.join(', ')}`
          } else if (monthlyPatternType === 'relative' && selectedOccurrences.length > 0 && selectedDays.length > 0) {
            const occLabels = selectedOccurrences.map((o) => OCCURRENCE_TYPE_LABELS[o]).join(', ')
            const dayLabels = selectedDays.map((d) => DAY_OF_WEEK_FULL_LABELS[d]).join(', ')
            yearlyDesc += ` (${occLabels} ${dayLabels})`
          }
          parts.push(yearlyDesc)
        } else {
          parts.push('Yearly')
        }
        break
    }

    // Time window
    if (startTime || endTime) {
      if (startTime && endTime) {
        parts.push(`${startTime} - ${endTime}`)
      } else if (startTime) {
        parts.push(`Starting at ${startTime}`)
      } else if (endTime) {
        parts.push(`Until ${endTime}`)
      }
    }

    return parts.join(' | ') || 'No schedule configured'
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Quick Templates & Preview */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}
        >
          Quick Templates
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PreviewIcon />}
          onClick={() => setPreviewOpen(true)}
        >
          Preview Occurrences
        </Button>
        <Menu
          anchorEl={templateMenuAnchor}
          open={Boolean(templateMenuAnchor)}
          onClose={() => setTemplateMenuAnchor(null)}
        >
          {TIME_SCHEDULE_TEMPLATES.map((template) => (
            <MenuItem key={template.name} onClick={() => applyTemplate(template)}>
              <ListItemText primary={template.name} secondary={template.description} />
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Preview */}
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          Schedule Preview
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {getDescription()}
        </Typography>
      </Paper>

      <Divider />

      {/* Section 1: Date Range */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Date Range (Optional)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={startDate ? startDate.split('T')[0] : ''}
            onChange={(e) => updateConfig({ startDate: e.target.value ? `${e.target.value}T00:00:00` : undefined })}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      const input = (e.currentTarget.parentElement?.parentElement?.querySelector('input') as HTMLInputElement)
                      input?.showPicker?.()
                    }}
                    edge="end"
                  >
                    <CalendarTodayIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={endDate ? endDate.split('T')[0] : ''}
            onChange={(e) => updateConfig({ endDate: e.target.value ? `${e.target.value}T23:59:59` : undefined })}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      const input = (e.currentTarget.parentElement?.parentElement?.querySelector('input') as HTMLInputElement)
                      input?.showPicker?.()
                    }}
                    edge="end"
                  >
                    <CalendarTodayIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Leave empty for no date restrictions
        </Typography>
      </Box>

      {/* Section 2: Daily Time Window */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Daily Time Window (Optional)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Start Time"
            type="time"
            size="small"
            value={startTime || ''}
            onChange={(e) => updateConfig({ startTime: e.target.value || undefined })}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      const input = (e.currentTarget.parentElement?.parentElement?.querySelector('input') as HTMLInputElement)
                      input?.showPicker?.()
                    }}
                    edge="end"
                  >
                    <AccessTimeIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="End Time"
            type="time"
            size="small"
            value={endTime || ''}
            onChange={(e) => updateConfig({ endTime: e.target.value || undefined })}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      const input = (e.currentTarget.parentElement?.parentElement?.querySelector('input') as HTMLInputElement)
                      input?.showPicker?.()
                    }}
                    edge="end"
                  >
                    <AccessTimeIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          The hours during which access is allowed each valid day
        </Typography>
      </Box>

      <Divider />

      {/* Section 3: Frequency */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Recurrence Pattern
        </Typography>
        <ToggleButtonGroup
          value={frequency}
          exclusive
          onChange={handleFrequencyChange}
          size="small"
          sx={{ flexWrap: 'wrap' }}
        >
          <ToggleButton value={ScheduleFrequency.OneTime}>One Time</ToggleButton>
          <ToggleButton value={ScheduleFrequency.Daily}>Daily</ToggleButton>
          <ToggleButton value={ScheduleFrequency.Weekly}>Weekly</ToggleButton>
          <ToggleButton value={ScheduleFrequency.Monthly}>Monthly</ToggleButton>
          <ToggleButton value={ScheduleFrequency.Yearly}>Yearly</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Section 4: Day Selection (for Daily/Weekly) */}
      <Collapse in={frequency === ScheduleFrequency.Daily || frequency === ScheduleFrequency.Weekly || frequency === ScheduleFrequency.Monthly}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Days of Week
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {Object.values(DayOfWeek)
              .filter((v) => typeof v === 'number')
              .map((day) => (
                <Chip
                  key={day}
                  label={DAY_OF_WEEK_LABELS[day as DayOfWeek]}
                  onClick={() => toggleDay(day as DayOfWeek)}
                  color={selectedDays.includes(day as DayOfWeek) ? 'primary' : 'default'}
                  variant={selectedDays.includes(day as DayOfWeek) ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="text" onClick={selectWeekdays}>
              Weekdays
            </Button>
            <Button size="small" variant="text" onClick={selectWeekends}>
              Weekends
            </Button>
            <Button size="small" variant="text" onClick={selectAllDays}>
              All Days
            </Button>
          </Box>
        </Box>
      </Collapse>

      {/* Section 5: Monthly Pattern */}
      <Collapse in={frequency === ScheduleFrequency.Monthly}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Monthly Pattern
          </Typography>
          <ToggleButtonGroup
            value={monthlyPatternType}
            exclusive
            onChange={(_, v) => v && updateConfig({ monthlyPatternType: v, selectedMonthDays: [], selectedOccurrences: [] })}
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="specific">Specific Days</ToggleButton>
            <ToggleButton value="relative">Relative (e.g., First Monday)</ToggleButton>
          </ToggleButtonGroup>

          <Collapse in={monthlyPatternType === 'specific'}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Select days of the month (1-31)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <Chip
                    key={day}
                    label={day}
                    onClick={() => toggleMonthDay(day)}
                    color={selectedMonthDays.includes(day) ? 'primary' : 'default'}
                    variant={selectedMonthDays.includes(day) ? 'filled' : 'outlined'}
                    size="small"
                    sx={{ minWidth: 36 }}
                  />
                ))}
              </Box>
            </Box>
          </Collapse>

          <Collapse in={monthlyPatternType === 'relative'}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Select occurrence position
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {Object.values(OccurrenceType)
                  .filter((v) => typeof v === 'number')
                  .map((occurrence) => (
                    <Chip
                      key={occurrence}
                      label={OCCURRENCE_TYPE_LABELS[occurrence as OccurrenceType]}
                      onClick={() => toggleOccurrence(occurrence as OccurrenceType)}
                      color={selectedOccurrences.includes(occurrence as OccurrenceType) ? 'primary' : 'default'}
                      variant={selectedOccurrences.includes(occurrence as OccurrenceType) ? 'filled' : 'outlined'}
                      size="small"
                    />
                  ))}
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Collapse>

      {/* Section 6: Yearly - Month and Day Selection */}
      <Collapse in={frequency === ScheduleFrequency.Yearly}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Months
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {MONTH_SHORT_LABELS.map((month, index) => (
                <Chip
                  key={month}
                  label={month}
                  onClick={() => toggleMonth(index + 1)}
                  color={selectedMonths.includes(index + 1) ? 'primary' : 'default'}
                  variant={selectedMonths.includes(index + 1) ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Box>
          </Box>

          {/* Day Pattern Selection for Yearly */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Day Pattern (Optional)
            </Typography>
            <ToggleButtonGroup
              value={monthlyPatternType}
              exclusive
              onChange={(_, v) => v && updateConfig({ monthlyPatternType: v, selectedMonthDays: [], selectedOccurrences: [], selectedDays: [] })}
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="specific">Specific Days</ToggleButton>
              <ToggleButton value="relative">Relative (e.g., First Monday)</ToggleButton>
            </ToggleButtonGroup>

            <Collapse in={monthlyPatternType === 'specific'}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Select days of the month (1-31)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <Chip
                      key={day}
                      label={day}
                      onClick={() => toggleMonthDay(day)}
                      color={selectedMonthDays.includes(day) ? 'primary' : 'default'}
                      variant={selectedMonthDays.includes(day) ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ minWidth: 36 }}
                    />
                  ))}
                </Box>
              </Box>
            </Collapse>

            <Collapse in={monthlyPatternType === 'relative'}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Select occurrence position
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {Object.values(OccurrenceType)
                    .filter((v) => typeof v === 'number')
                    .map((occurrence) => (
                      <Chip
                        key={occurrence}
                        label={OCCURRENCE_TYPE_LABELS[occurrence as OccurrenceType]}
                        onClick={() => toggleOccurrence(occurrence as OccurrenceType)}
                        color={selectedOccurrences.includes(occurrence as OccurrenceType) ? 'primary' : 'default'}
                        variant={selectedOccurrences.includes(occurrence as OccurrenceType) ? 'filled' : 'outlined'}
                        size="small"
                      />
                    ))}
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Select day of week
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.values(DayOfWeek)
                    .filter((v) => typeof v === 'number')
                    .map((day) => (
                      <Chip
                        key={day}
                        label={DAY_OF_WEEK_LABELS[day as DayOfWeek]}
                        onClick={() => toggleDay(day as DayOfWeek)}
                        color={selectedDays.includes(day as DayOfWeek) ? 'primary' : 'default'}
                        variant={selectedDays.includes(day as DayOfWeek) ? 'filled' : 'outlined'}
                        size="small"
                      />
                    ))}
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Collapse>

      <Divider />

      {/* Section 7: Timezone */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Timezone Settings
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={useUserLocalTime}
              onChange={(e) => updateConfig({ useUserLocalTime: e.target.checked, timezone: undefined, utcOffset: undefined })}
            />
          }
          label="Use user's local time"
        />
        <Collapse in={!useUserLocalTime}>
          <Autocomplete
            size="small"
            options={TIMEZONE_OPTIONS}
            value={TIMEZONE_OPTIONS.find((tz) => tz.id === timezone) || null}
            onChange={(_, newValue: TimezoneOption | null) => {
              updateConfig({
                timezone: newValue?.id,
                utcOffset: newValue?.utcOffset,
              })
            }}
            getOptionLabel={(option) => `${option.label} (${formatUtcOffset(option.utcOffset)})`}
            groupBy={(option) => option.region}
            filterOptions={(options, { inputValue }) => {
              const searchLower = inputValue.toLowerCase()
              return options.filter(
                (option) =>
                  option.label.toLowerCase().includes(searchLower) ||
                  option.id.toLowerCase().includes(searchLower) ||
                  option.region.toLowerCase().includes(searchLower) ||
                  formatUtcOffset(option.utcOffset).toLowerCase().includes(searchLower)
              )
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2, flexShrink: 0 }}>
                    {formatUtcOffset(option.utcOffset)}
                  </Typography>
                </Box>
              </li>
            )}
            renderGroup={(params) => (
              <li key={params.key}>
                <Typography
                  variant="overline"
                  sx={{
                    position: 'sticky',
                    top: -8,
                    px: 2,
                    py: 0.5,
                    bgcolor: 'background.paper',
                    fontWeight: 'bold',
                    color: 'primary.main',
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  {params.group}
                </Typography>
                <ul style={{ padding: 0 }}>{params.children}</ul>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Timezone"
                placeholder="Search by city, country, or UTC offset..."
                helperText="Start typing to search timezones"
              />
            )}
            sx={{ mt: 1 }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            ListboxProps={{ sx: { maxHeight: 300 } }}
          />
        </Collapse>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {useUserLocalTime
            ? 'Schedule will be evaluated in each user\'s local timezone'
            : timezone
              ? `Schedule will be evaluated in ${TIMEZONE_OPTIONS.find((tz) => tz.id === timezone)?.label || timezone}`
              : 'Select a timezone for this schedule'}
        </Typography>
      </Box>

      {/* Validation alerts */}
      {frequency === ScheduleFrequency.Weekly && selectedDays.length === 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Select at least one day of the week
        </Alert>
      )}
      {frequency === ScheduleFrequency.Monthly && monthlyPatternType === 'specific' && selectedMonthDays.length === 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Select at least one day of the month
        </Alert>
      )}
      {frequency === ScheduleFrequency.Monthly && monthlyPatternType === 'relative' && (selectedOccurrences.length === 0 || selectedDays.length === 0) && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Select at least one occurrence position and one day of the week
        </Alert>
      )}
      {frequency === ScheduleFrequency.Yearly && selectedMonths.length === 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Select at least one month
        </Alert>
      )}
      {frequency === ScheduleFrequency.Yearly && monthlyPatternType === 'relative' && (selectedOccurrences.length > 0 || selectedDays.length > 0) && (selectedOccurrences.length === 0 || selectedDays.length === 0) && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Select both occurrence position and day of the week for relative pattern
        </Alert>
      )}

      {/* Preview Occurrences Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PreviewIcon />
          Next 10 Occurrences
        </DialogTitle>
        <DialogContent dividers>
          {nextOccurrences.length === 0 ? (
            <Alert severity="info">
              No upcoming occurrences found based on the current configuration.
              {frequency === ScheduleFrequency.OneTime && !startDate && ' Set a start date for one-time events.'}
            </Alert>
          ) : (
            <List dense>
              {nextOccurrences.map((date, index) => (
                <ListItem key={index}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <EventIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={date.toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                    secondary={
                      startTime && endTime
                        ? `${startTime} - ${endTime}`
                        : startTime
                          ? `Starting at ${startTime}`
                          : endTime
                            ? `Until ${endTime}`
                            : 'All day'
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
          {nextOccurrences.length > 0 && nextOccurrences.length < 10 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Only {nextOccurrences.length} occurrence{nextOccurrences.length !== 1 ? 's' : ''} found within the next 2 years
              {endDate && ' or before the end date'}.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TimeScheduleConfigPanel
